/* ─────────────────────────────────────────────────────────────
   BotonMigrarIdOficial.jsx — admin only
   Migración única: sube el Excel "ruta_puntos_corregida.xlsx"
   (cruce CLAVE vieja de pipas -> ID oficial Anexo 2026) y agrega
   id_oficial/zona_oficial_contratos a los puntos de ruta_puntos
   que ya existen, SIN tocar lo que ve el chofer/bot.
   ───────────────────────────────────────────────────────────── */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BotonMigrarIdOficial() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user?.rol !== "admin") return null;

  return (
    <>
      <div className="mt-6 p-4 bg-white border border-slate-200 rounded-lg" data-testid="seccion-migrar-id-oficial">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-bold text-slate-800">
              <i className="fa-solid fa-link mr-2 text-indigo-600"/>
              Migrar ID oficial (Anexo 2026)
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Migración de una sola vez: agrega el ID oficial y la zona de contratos a los puntos
              que ya tienen ruta. No cambia nada de lo que ve el chofer ni el bot.
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="btn-abrir-migrar-id-oficial"
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded px-4 py-2 text-sm"
          >
            <i className="fa-solid fa-upload mr-2"/> Subir cruce
          </button>
        </div>
      </div>

      {open && <ModalMigrar onClose={() => setOpen(false)}/>}
    </>
  );
}


function ModalMigrar({ onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [aplicado, setAplicado] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      toast.error("Solo archivos .xlsx");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("El archivo supera 20 MB");
      return;
    }
    setFile(f);
    setPreview(null);
    setAplicado(null);
  };

  const _enviar = async (confirmar) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (confirmar) fd.append("confirmar", "true");
      const r = await axios.post(`${API}/admin/migrar-id-oficial-ruta-puntos/desde-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (confirmar) {
        setAplicado(r.data);
        toast.success(`Migración aplicada: ${r.data.actualizados} actualizados, ${r.data.sin_coincidencia} sin coincidencia`);
      } else {
        setPreview(r.data);
        if (r.data.ok && (r.data.items_parseados || 0) > 0) {
          toast.success(`Vista previa lista: ${r.data.items_parseados} puntos detectados`);
        } else {
          toast.error(r.data.mensaje || "El parser no encontró puntos válidos.");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al procesar el Excel");
    } finally {
      setBusy(false);
    }
  };

  const vistaPrevia = () => _enviar(false);
  const aplicar = () => _enviar(true);

  return (
    <div
      onClick={onClose}
      data-testid="modal-migrar-id-oficial-bg"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.7)",
        zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="modal-migrar-id-oficial"
        style={{
          background: "white", borderRadius: 14, width: "100%", maxWidth: 760,
          maxHeight: "92vh", overflow: "auto",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
      >
        <header style={{ padding: "16px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
              <i className="fa-solid fa-link mr-2 text-indigo-600"/>
              Migrar ID oficial a ruta_puntos
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
              Sube el archivo <code>ruta_puntos_corregida.xlsx</code> que te compartió Claude
            </div>
          </div>
          <button onClick={onClose} aria-label="Cerrar" data-testid="modal-migrar-cerrar"
                  style={{ background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: "#64748B" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        <section style={{ padding: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 6 }}>
              1) Selecciona el archivo
            </label>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onPick}
              data-testid="input-file-migrar"
              style={{ width: "100%", padding: "10px 12px", border: "1px dashed #94A3B8", borderRadius: 8, fontSize: 14, background: "#F8FAFC" }}
            />
            {file && (
              <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }} data-testid="file-info-migrar">
                <i className="fa-solid fa-paperclip mr-1"/> {file.name} · {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>

          {file && !aplicado && (
            <div style={{ marginBottom: 16 }}>
              <button
                onClick={vistaPrevia}
                disabled={busy}
                data-testid="btn-vista-previa-migrar"
                style={{
                  background: "#1D4ED8", color: "white", border: 0, padding: "10px 16px",
                  borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: busy ? "wait" : "pointer",
                }}
              >
                <i className="fa-solid fa-magnifying-glass mr-2"/>
                {busy && !preview ? "Procesando…" : "Vista previa (dry-run)"}
              </button>
            </div>
          )}

          {preview && !aplicado && <ResumenPreview preview={preview}/>}

          {preview && preview.ok && (preview.items_parseados || 0) > 0 && !aplicado && (
            <div style={{ marginTop: 16, padding: 12, background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8 }}>
              <div style={{ fontSize: 13, color: "#92400E", marginBottom: 10 }}>
                <i className="fa-solid fa-triangle-exclamation mr-2"/>
                <strong>¿Estás seguro?</strong> Esto agregará id_oficial/zona_oficial_contratos en producción.
                No toca zona del chofer, día, orden, pipa ni nombre.
              </div>
              <button
                onClick={aplicar}
                disabled={busy}
                data-testid="btn-aplicar-migracion"
                style={{
                  background: "#15803D", color: "white", border: 0, padding: "12px 18px",
                  borderRadius: 8, fontWeight: 800, fontSize: 14, cursor: busy ? "wait" : "pointer",
                }}
              >
                <i className="fa-solid fa-check mr-2"/>
                {busy ? "Aplicando…" : `Aplicar migración (${preview.actualizados} actualizar)`}
              </button>
            </div>
          )}

          {aplicado && <ResumenAplicado aplicado={aplicado} onClose={onClose}/>}
        </section>
      </div>
    </div>
  );
}


function ResumenPreview({ preview }) {
  return (
    <div data-testid="preview-resumen-migrar" style={{ background: "#F8FAFC", padding: 14, borderRadius: 8, border: "1px solid #E2E8F0" }}>
      <div style={{ fontWeight: 800, color: "#0F172A", marginBottom: 8, fontSize: 14 }}>
        <i className="fa-solid fa-eye mr-2 text-blue-600"/> Vista previa (no aplica cambios)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }} data-testid="preview-conteos-migrar">
        <Stat label="Puntos detectados" value={preview.items_parseados || 0} color="#0F172A"/>
        <Stat label="A actualizar" value={preview.actualizados || 0} color="#9A3412"/>
        <Stat label="Sin coincidencia" value={preview.sin_coincidencia || 0} color="#B91C1C"/>
      </div>
      {(preview.detalles_sin_coincidencia || []).length > 0 && (
        <div style={{ padding: 8, background: "#FEE2E2", borderRadius: 6, fontSize: 12, color: "#B91C1C" }} data-testid="preview-sin-coincidencia">
          <strong>Sin coincidencia en ruta_puntos (revisar manualmente, no se tocan):</strong>
          <ul style={{ margin: "4px 0 0 16px", padding: 0, maxHeight: 160, overflow: "auto" }}>
            {preview.detalles_sin_coincidencia.map((d, i) => (
              <li key={i}>{d.nombre} ({d.zona_id})</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function ResumenAplicado({ aplicado, onClose }) {
  return (
    <div data-testid="aplicado-resumen-migrar" style={{ background: "#DCFCE7", border: "1px solid #15803D", borderRadius: 8, padding: 16 }}>
      <div style={{ fontWeight: 800, color: "#14532D", fontSize: 16, marginBottom: 10 }}>
        <i className="fa-solid fa-circle-check mr-2"/>
        Migración aplicada con éxito
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
        <Stat label="Puntos procesados" value={aplicado.items_parseados || 0} color="#0F172A"/>
        <Stat label="Actualizados" value={aplicado.actualizados || 0} color="#15803D"/>
        <Stat label="Sin coincidencia" value={aplicado.sin_coincidencia || 0} color="#B91C1C"/>
      </div>
      <button
        onClick={onClose}
        data-testid="btn-cerrar-aplicado-migrar"
        style={{ background: "#1D4ED8", color: "white", border: 0, padding: "10px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
      >
        Cerrar
      </button>
    </div>
  );
}


function Stat({ label, value, color }) {
  return (
    <div style={{ background: "white", borderRadius: 6, padding: 10, textAlign: "center", border: "1px solid #E2E8F0" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 10, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
    </div>
  );
}
